import React from 'react';

/** Green map pin with up chevron — Est. pickup */
export function EstPickupPinIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10.5c0 6.2-8 11-8 11s-8-4.8-8-11a8 8 0 1 1 16 0z" />
      <path d="M9.5 11.25 12 8.75l2.5 2.5" />
    </svg>
  );
}

/** Red map pin with down chevron — Est. dropoff */
export function EstDropoffPinIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10.5c0 6.2-8 11-8 11s-8-4.8-8-11a8 8 0 1 1 16 0z" />
      <path d="M9.5 9.75 12 12.25l2.5-2.5" />
    </svg>
  );
}

/** Lucide clock-arrow — Load (green): <- */
export function LoadClockIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 6v6l2 1" />
      <path d="M13.5 21.885A10 10 0 1 1 22 12" />
      <path d="M14 18h8" />
      <path d="m18 22 -4-4 4-4" />
    </svg>
  );
}

/** Clock-arrow — Unload (red): -> */
export function UnloadClockIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 6v6l2 1" />
      <path d="M13.5 21.885A10 10 0 1 1 22 12" />
      <path d="M14 18h8" />
      <path d="m18 22 4-4-4-4" />
    </svg>
  );
}

function formatEstimateTime(iso, timezone) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    });
  } catch {
    return String(iso);
  }
}

/**
 * Fixed-slot estimate strip so icons + times share one baseline.
 * [pin] HH:mm [clock] N min
 */
function EstimateMetaGrid({
  pinIcon,
  pinTitle,
  time,
  clockIcon,
  clockTitle,
  minutes,
}) {
  const showTime = time != null;
  const showMins = minutes != null;
  if (!showTime && !showMins) return null;

  return (
    <span
      className="inline-flex h-4 shrink-0 items-center gap-1 text-[10px] leading-none text-main-secondary"
      aria-label={[pinTitle, time, clockTitle, showMins ? `${minutes} min` : null]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
        title={pinTitle}
        aria-label={pinTitle}
      >
        {/* Pin tip hangs low in the SVG — nudge up so the head lines up with the clock */}
        {showTime ? (
          <span className="inline-flex -translate-y-px">{pinIcon}</span>
        ) : (
          <span className="h-3.5 w-3.5" aria-hidden />
        )}
      </span>
      <span className="inline-block w-[2.75rem] shrink-0 tabular-nums tracking-tight">
        {showTime ? time : ''}
      </span>
      <span
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
        title={clockTitle}
        aria-label={clockTitle}
      >
        {showMins ? clockIcon : <span className="h-3.5 w-3.5" aria-hidden />}
      </span>
      <span className="inline-block w-[2.25rem] shrink-0 tabular-nums tracking-tight whitespace-nowrap">
        {showMins ? `${minutes} min` : ''}
      </span>
    </span>
  );
}

/**
 * Inline next to pickup schedule — aligned columns with dropoff row.
 */
export function PickupEstimateMeta({ trip, timezone = 'America/Los_Angeles' }) {
  if (trip?.is_break === true) return null;
  const details = trip?.trip_details;
  const approxPickup = details?.approximate_pickup_time;
  const loadMin = details?.load_time_minutes ?? trip?.loadTime;
  const time = formatEstimateTime(approxPickup, timezone);

  return (
    <EstimateMetaGrid
      pinTitle="Est. pickup"
      pinIcon={<EstPickupPinIcon className="h-3.5 w-3.5 text-main-success" />}
      time={time}
      clockTitle="Load"
      clockIcon={<LoadClockIcon className="h-3.5 w-3.5 text-main-success" />}
      minutes={loadMin}
    />
  );
}

/**
 * Inline next to dropoff appointment — same column template as pickup.
 */
export function DropoffEstimateMeta({ trip, timezone = 'America/Los_Angeles' }) {
  if (trip?.is_break === true) return null;
  const details = trip?.trip_details;
  const approxDropoff = details?.approximate_dropoff_time;
  const unloadMin = details?.unload_time_minutes ?? trip?.unloadTime;
  const time = formatEstimateTime(approxDropoff, timezone);

  return (
    <EstimateMetaGrid
      pinTitle="Est. dropoff"
      pinIcon={<EstDropoffPinIcon className="h-3.5 w-3.5 text-main-danger" />}
      time={time}
      clockTitle="Unload"
      clockIcon={<UnloadClockIcon className="h-3.5 w-3.5 text-main-danger" />}
      minutes={unloadMin}
    />
  );
}
